import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { fieldErrorsOf } from '@/api/client';
import { useClientInvitationMutations } from '@/api/hooks';
import type { ClientOrg, Invitation } from '@/api/schemasMembers';
import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Segmented } from '@/features/access/bits';
import { deliveryToast } from '@/features/members/membersModel';
import { CLIENT_ROLE_OPTIONS, validateClientInvite, type ClientInviteValues, type FieldErrors } from './clientLifecycleModel';

const EMPTY: ClientInviteValues = { email: '', name: '', role: 'member' };

function useInviteForm(org: ClientOrg, pending: Invitation[] | undefined) {
  const { invite } = useClientInvitationMutations(org.id);
  const [values, setValues] = useState<ClientInviteValues>(EMPTY);
  const [touched, setTouched] = useState(false);
  const [server, setServer] = useState<Record<string, string>>({});
  const [idempotencyKey, setKey] = useState(() => crypto.randomUUID());
  const local: FieldErrors<'email' | 'name'> = validateClientInvite(values, org.emailDomains, pending);
  const errors: Record<string, string | undefined> = touched ? { ...server, ...local } : server;
  const set = (patch: Partial<ClientInviteValues>) => { setServer({}); setValues((v) => ({ ...v, ...patch })); };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (Object.keys(local).length) return;
    try {
      const body = { email: values.email.trim().toLowerCase(), orgRole: values.role, ...(values.name.trim() ? { name: values.name.trim() } : {}) };
      const created = await invite.mutateAsync({ body, idempotencyKey });
      const sent = deliveryToast(created);
      const joins = `They join ${org.name} as ${values.role === 'admin' ? 'an admin' : 'a member'}.`;
      if (sent.kind === 'warning') toast.warning(sent.title, { description: 'Use Resend in the list below.' });
      else toast.success(sent.title, { description: `${joins} ${sent.description ?? 'The link works once and expires in 7 days.'}` });
      setValues(EMPTY);
      setTouched(false);
      setKey(crypto.randomUUID());
    } catch (err) {
      setServer(fieldErrorsOf(err));
    }
  };
  return { values, errors, set, submit, setTouched, invite, hasServerErrors: Object.keys(server).length > 0 };
}

/** Invite an admin or a member whose email is in the client's domains (`POST /platform/orgs/{orgId}/invitations`). */
export function ClientInviteForm({ org, pending }: { org: ClientOrg; pending: Invitation[] | undefined }) {
  const f = useInviteForm(org, pending);
  const domains = org.emailDomains.length ? org.emailDomains.map((d) => `@${d}`).join(', ') : 'its domains';
  return (
    <form data-testid="client-invite-form" onSubmit={(e) => void f.submit(e)} noValidate aria-label="Invite people" className="flex flex-col gap-4 rounded-lg border p-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id="client-invite-email" label="Email" required error={f.errors.email} hint={`An address at ${domains}`}>
          <Input data-testid="client-invite-email" type="email" autoComplete="off" value={f.values.email} onChange={(e) => f.set({ email: e.target.value })} onBlur={() => f.values.email && f.setTouched(true)} placeholder={`name@${org.emailDomains[0] ?? 'company.com'}`} {...fieldA11y('client-invite-email', f.errors.email, 'hint')} />
        </FormField>
        <FormField id="client-invite-name" label="Name" error={f.errors.name} hint="Optional: used in the email">
          <Input data-testid="client-invite-name" value={f.values.name} maxLength={120} onChange={(e) => f.set({ name: e.target.value })} {...fieldA11y('client-invite-name', f.errors.name, 'hint')} />
        </FormField>
      </div>
      <div className="flex flex-col gap-1.5">
        <Segmented label="Role" testId="client-invite-role" value={f.values.role} onChange={(role) => f.set({ role })} options={CLIENT_ROLE_OPTIONS.map((o) => ({ value: o.value, label: o.label, title: o.description }))} />
        <p className="text-xs text-subtle">{CLIENT_ROLE_OPTIONS.find((o) => o.value === f.values.role)?.description}</p>
        {f.errors.orgRole ? <p className="text-xs text-destructive">{f.errors.orgRole}</p> : null}
      </div>
      {f.invite.error && !f.hasServerErrors ? <ProblemAlert error={f.invite.error} testId="client-invite-error" /> : null}
      <div className="flex justify-end">
        <Button type="submit" data-testid="client-invite-submit" disabled={f.invite.isPending} className="max-sm:h-11 max-sm:w-full">
          <Icon name="person_add" size={18} />
          {f.invite.isPending ? 'Sending…' : 'Send invitation'}
        </Button>
      </div>
    </form>
  );
}
